import CircularProgress from '@mui/material/CircularProgress'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogTitle from '@mui/material/DialogTitle'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useTheme } from '@mui/material/styles'
import React, { useState } from 'react'
import SaveButton from '../Buttons/SaveButton'
import CloseButton from '../IconButtons/CloseButton'
import colors from 'constants/colors'

/**
 * The extra props this HOC adds vary in shape depending on the wrapped modal, so they
 * are modelled as a loose record with the fields this file itself relies on made explicit.
 */
type SimpleModalProps = {
  onClose?(value?: unknown): void
  noEscClose?: boolean
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | false
  fullWidth?: boolean
  header?: React.ReactNode
  allowSubmit?: React.ReactNode
  valid?: boolean
  [key: string]: unknown
}

/**
 * Simple modal higher order component.
 *
 * @param ModalComponent - Modal component to wrap.
 * @returns Simple modal component.
 * @notExported
 */
const newWithSimpleModal = <TModalProps extends object>(ModalComponent: React.ComponentType<TModalProps>) => {
  const SimpleModal = (props: SimpleModalProps) => {
    const theme = useTheme()
    const fullScreen = useMediaQuery(theme.breakpoints.down('md'))
    const [submitLocally, setSubmitLocally] = useState<boolean>(false)
    const [loading, setLoading] = useState<boolean>(false)
    const [buttonLoading, setButtonLoading] = useState<boolean>(false)
    const [extraActions, setExtraActions] = useState<React.ReactNode>()

    return (
      <Dialog
        fullScreen={fullScreen}
        open
        aria-labelledby="form-dialog-title"
        onClose={() => {
          if (props.noEscClose) {
            return
          }
          props.onClose?.({})
        }}
        maxWidth={props.maxWidth}
        fullWidth={props.fullWidth}
        sx={{
          '.MuiDialog-paper': {
            backgroundColor: colors.secondaryBackground,
            borderRadius: 1,
          },
        }}
      >
        <DialogTitle sx={{ backgroundColor: colors.secondaryBackground }}>
          <span id="form-dialog-title" style={{ textTransform: 'none' }}>
            {props.header}
          </span>
          <CloseButton testId="button-simple-modal-close" clickAction={() => props.onClose?.({})} />
        </DialogTitle>
        {React.createElement(ModalComponent, {
          ...props,
          submitIndicator: submitLocally,
          resetSubmit: () => {
            setSubmitLocally(false)
            setButtonLoading(false)
          },
          setLoading,
          loading,
          setExtraActions: (actionItem: React.ReactNode) => setExtraActions(actionItem),
        } as unknown as React.Attributes & TModalProps)}
        <DialogActions sx={{ background: colors.secondaryBackground, paddingRight: '22px', paddingBottom: '22px' }}>
          {props.allowSubmit && (
            <SaveButton
              label={loading ? <CircularProgress /> : props.allowSubmit}
              valid={props.valid === undefined ? true : props.valid}
              clickAction={() => {
                setButtonLoading(true)
                setSubmitLocally(true)
              }}
              disabled={loading}
              loading={buttonLoading}
              testId="button-simple-modal-submit"
            />
          )}
          {extraActions}
        </DialogActions>
      </Dialog>
    )
  }
  return SimpleModal
}

export default newWithSimpleModal
